/**
 * 날씨 위치 — 외부 API 탭 안의 둘째 보기 (2026-09-21 · 콘솔 시안 v2 확정).
 *
 * ## ★★★ 이 표가 답하는 질문
 *
 * 「키는 넣었는데 **어느 회사의 어느 곳**이 날씨가 안 나오나」. 공급자 목록은 그것을 못 답한다 —
 * 키가 멀쩡해도 **주소가 시·도까지만 적힌 곳**은 날씨를 내지 않기 때문이다(틀린 값을 맞는 척 내지 않으려는 것).
 *
 * ## ★★ 회사별 묶음인 까닭
 *
 * 8444 는 **여러 회사를 관리하는 화면**이다. 곳만 쭉 늘어놓으면 「이 판교센터가 어느 회사 것인가」를
 * 다시 세어야 하고, 전화를 걸 상대도 회사 단위다. 그래서 회사 머리줄 아래에 그 회사의 곳이 선다.
 *
 * ## ⛔ 여기서 주소를 고치지 않는다
 *
 * 주소는 **그 회사의 8443 화면**(회사정보 · 사업장 상세)에서 그 회사 관리자가 고친다. 콘솔은
 * **어디가 막혔는지 보여 주는 자리**다. 시스템 관리자가 남의 회사 주소를 대신 바꾸면 그 회사의
 * 감사·이력이 콘솔 운영자 이름으로 남는다.
 *
 * ## 위치는 저장돼 있지 않다
 *
 * 서버가 주소에서 **그때 셈해** 준다(`resolveWeatherGrid`). 그래서 「확정」 단추가 없고, 주소를 고치면
 * 다음 조회부터 저절로 따라간다 — 사용자 결정 「확정으로 할 필요가 없다. 저장하면 그 위치로 보여주면 된다」.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from '../../../icons';
import {
  listWeatherGrid,
  type WeatherGridInstitution, type WeatherGridLocation, type WeatherGridPlace,
} from '../../../api/externalApis';
import { errText } from './ExternalApiEditModal';

/** 어디까지 맞췄나 — `sido` 는 도 전체를 대표하는 한 점이라 날씨를 내지 않는다. */
const LEVEL_LABEL: Record<string, string> = { dong: '동까지 맞음', sigungu: '구·군까지 맞음', sido: '시·도까지만 알아냄' };

/** ★ 세 갈래를 뭉치지 않는다 — 관리자가 할 일이 저마다 다르다. */
export function locationBadge(loc: WeatherGridLocation) {
  if (loc.kind === 'ok') return <span className="xa-badge xa-badge--ok">정상</span>;
  if (loc.kind === 'no_address') return <span className="xa-badge xa-badge--muted">주소 없음</span>;
  return <span className="xa-badge xa-badge--warn">위치를 알 수 없음</span>;
}

function locationCell(loc: WeatherGridLocation) {
  if (loc.kind === 'ok') {
    return (
      <span className="xa-cell">
        <b>{loc.regionName}</b>
        <span className="xa-grid__xy">{loc.nx}, {loc.ny} · {LEVEL_LABEL[loc.level] ?? loc.level}</span>
      </span>
    );
  }
  if (loc.kind === 'no_address') return <span className="xa-dim">—</span>;
  return (
    <span className="xa-dim">
      {loc.regionName
        ? `${loc.regionName}까지만 알아냄 — 날씨 없음`
        : '주소에서 지역을 못 찾음 — 날씨 없음'}
    </span>
  );
}

/** 관리자가 할 일 — 「고친다」와 「적는다」는 다른 일이다. */
function todo(loc: WeatherGridLocation): string | null {
  if (loc.kind === 'ok') return null;
  if (loc.kind === 'no_address') return '주소를 적어야 합니다';
  return '주소를 동 이름까지 적어야 합니다';
}

export interface WeatherGridRow extends WeatherGridPlace {
  key: string;
  /** 회사 주소인가(본사 사업장이 아니라 회사 그 자체의 주소). */
  isCompany: boolean;
  isHeadOffice: boolean;
  isActive: boolean;
}

function rowsOf(inst: WeatherGridInstitution): WeatherGridRow[] {
  return [
    { key: `c:${inst.institution_id}`, isCompany: true, isHeadOffice: false, isActive: true, ...inst.company },
    ...inst.worksites.map((w) => ({
      key: `w:${w.id}`, isCompany: false, isHeadOffice: w.is_head_office, isActive: w.is_active,
      name: w.name, address: w.address, location: w.location,
    })),
  ];
}

const needsFix = (r: WeatherGridRow) => r.location.kind !== 'ok';

export interface WeatherGridPanelProps {
  /** 툴바의 검색 글자 — 화면(`ExternalApiSection`)이 쥐고 있다. */
  q: string;
  /** 「고칠 곳만」 거르개. */
  onlyBad: boolean;
  /** 세어 본 수를 화면에 알린다 — 좌 분류 칸과 KPI 가 같은 수를 쓴다. */
  onCount?: (n: { places: number; bad: number }) => void;
}

export function WeatherGridPanel({ q, onlyBad, onCount }: WeatherGridPanelProps) {
  const [institutions, setInstitutions] = useState<WeatherGridInstitution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await listWeatherGrid();
      setInstitutions(r.institutions);
    } catch (e: unknown) {
      setError(errText(e, '날씨 위치를 불러오지 못했습니다'));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  /** 회사마다 「그 회사의 줄」을 만들고, 거른 뒤에도 **회사 묶음**을 유지한다. */
  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return institutions.map((inst) => {
      const all = rowsOf(inst);
      const hit = (r: WeatherGridRow) => !needle
        || inst.name.toLowerCase().includes(needle)
        || r.name.toLowerCase().includes(needle)
        || (r.address ?? '').toLowerCase().includes(needle);
      return {
        inst,
        all,
        rows: all.filter((r) => hit(r) && (!onlyBad || needsFix(r))),
        bad: all.filter(needsFix).length,
      };
    }).filter((g) => g.rows.length > 0);
  }, [institutions, q, onlyBad]);

  const totals = useMemo(() => {
    const all = institutions.flatMap(rowsOf);
    return { places: all.length, bad: all.filter(needsFix).length };
  }, [institutions]);
  useEffect(() => { onCount?.(totals); }, [onCount, totals]);

  if (loading) return <div className="cl-empty">불러오는 중…</div>;
  if (error) {
    return (
      <div className="xa-band xa-band--bad" role="alert">
        <AlertTriangle size={18} aria-hidden />
        <span className="xa-band__t"><b>{error}</b></span>
      </div>
    );
  }
  if (groups.length === 0) {
    return <div className="cl-empty">{onlyBad ? '고칠 곳이 없습니다 — 모든 곳의 위치를 알고 있습니다' : '보여 줄 회사가 없습니다'}</div>;
  }

  return (
    <>
      {totals.bad > 0 && (
        <div className="xa-band xa-band--warn" role="status">
          <AlertTriangle size={18} aria-hidden />
          <span className="xa-band__t">
            <b>{totals.bad}곳의 날씨 위치를 알 수 없습니다</b>
            <span>주소는 <b>그 회사의 8443 화면</b>(회사정보 · 사업장)에서 고칩니다 — 콘솔은 어디가 막혔는지만 보여 줍니다.</span>
          </span>
        </div>
      )}
      <table className="cl-table xa-grid">
        <thead>
          <tr>
            <th>곳</th>
            <th>주소</th>
            <th>날씨 위치</th>
            <th>상태</th>
            <th>할 일</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <Fragment key={g.inst.institution_id}>
              {/* ★ 회사 머리줄 — 「이 곳이 어느 회사 것인가」를 다시 세지 않게 한다. */}
              <tr className="xa-grid__co">
                <th colSpan={5} scope="colgroup">
                  <b>{g.inst.name}</b>
                  <span>
                    {g.inst.code ? `${g.inst.code} · ` : ''}곳 {g.all.length}
                    {g.bad > 0 ? ` · 고칠 곳 ${g.bad}` : ' · 정상'}
                  </span>
                </th>
              </tr>
              {g.rows.map((r) => (
                <tr key={r.key}>
                  <td>
                    <span className="xa-cell">
                      <b>{r.name}</b>
                      <span>
                        {r.isCompany ? '회사 주소' : r.isHeadOffice ? '사업장 · 본사' : '사업장'}
                        {!r.isCompany && !r.isActive ? ' · 쓰지 않음' : ''}
                      </span>
                    </span>
                  </td>
                  <td>{r.address ? <span className="xa-grid__addr">{r.address}</span> : <span className="xa-dim">—</span>}</td>
                  <td>{locationCell(r.location)}</td>
                  <td>{locationBadge(r.location)}</td>
                  <td>{todo(r.location) ? <span className="xa-dim">{todo(r.location)}</span> : <span className="xa-dim">—</span>}</td>
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </>
  );
}
